/**
 * Archive filter: dropdown filters, URL state and load more.
 * Server side: includes/archive-filters.php (action pk_child_archive_filter).
 */

const ACTION = "pk_child_archive_filter";
const DATE_FIELDS = ["date_from", "date_to"];

const initArchiveFilter = (root) => {
    const { archiveFilter, ajaxUrl, allLabel, fromLabel, toLabel } = root.dataset;
    const form = root.querySelector("[data-filter-form]");
    const results = root.querySelector("[data-results]");
    const empty = root.querySelector("[data-empty]");
    const more = root.querySelector("[data-load-more]");
    const chips = root.querySelector("[data-chips]");

    if (!form || !results) return;

    let page = 1;
    let requestId = 0;

    const readState = () => {
        const state = {};
        for (const [name, value] of new FormData(form)) {
            if (value) (state[name] ??= []).push(value);
        }
        return state;
    };

    const writeState = (params) => {
        form.querySelectorAll("input").forEach((input) => {
            const values = (params.get(input.name) ?? "").split(",");
            if (input.type === "checkbox") input.checked = values.includes(input.value);
            else input.value = params.get(input.name) ?? "";
        });
    };

    const formatDate = (value) =>
        new Date(`${value}T00:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "short" });

    const closeIcon = () => {
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.setAttribute("viewBox", "0 0 12 12");
        svg.setAttribute("aria-hidden", "true");
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("d", "M1 1L11 11M11 1L1 11");
        path.setAttribute("stroke", "currentColor");
        path.setAttribute("stroke-width", "1.5");
        svg.append(path);
        return svg;
    };

    const renderChips = () => {
        if (!chips) return;
        chips.replaceChildren();

        const addChip = (text, name, value) => {
            const chip = document.createElement(name ? "button" : "span");
            chip.className = "filter-chip";
            chip.append(text);
            if (name) {
                chip.type = "button";
                chip.dataset.filter = name;
                chip.dataset.value = value;
                chip.append(closeIcon());
            }
            chips.append(chip);
        };

        for (const input of form.querySelectorAll("input")) {
            if (input.type === "checkbox" && input.checked) {
                addChip(input.closest("label").textContent.trim(), input.name, input.value);
            } else if (DATE_FIELDS.includes(input.name) && input.value) {
                const label = input.name === "date_from" ? fromLabel : toLabel;
                addChip(`${label} ${formatDate(input.value)}`, input.name, input.value);
            }
        }

        if (!chips.children.length && allLabel) addChip(allLabel);
    };

    const updateUrl = (push) => {
        const params = new URLSearchParams();
        for (const [name, values] of Object.entries(readState())) params.set(name, values.join(","));

        const url = `${location.pathname}${params.size ? `?${params}` : ""}`;
        history[push ? "pushState" : "replaceState"](null, "", url);
    };

    const load = async (nextPage, append) => {
        const id = ++requestId;
        const body = new URLSearchParams({ action: ACTION, filter_key: archiveFilter, paged: nextPage });

        for (const [name, values] of Object.entries(readState())) {
            values.forEach((value) => body.append(`filters[${name}][]`, value));
        }

        root.classList.add("is-loading");

        try {
            const response = await fetch(ajaxUrl, { method: "POST", body });
            const json = await response.json();
            if (id !== requestId || !json.success) return;

            const { html, total, total_pages: totalPages } = json.data;
            if (append) results.insertAdjacentHTML("beforeend", html);
            else results.innerHTML = html;
            window.pkButtonTextAnimation?.(results);

            page = nextPage;
            if (empty) empty.hidden = total > 0;
            if (more) more.hidden = page >= totalPages;
        } catch (error) {
            console.error(error);
        } finally {
            if (id === requestId) root.classList.remove("is-loading");
        }
    };

    const applyFilters = () => {
        renderChips();
        updateUrl(true);
        load(1, false);
    };

    form.addEventListener("change", applyFilters);
    form.addEventListener("submit", (event) => event.preventDefault());

    chips?.addEventListener("click", (event) => {
        const chip = event.target.closest("[data-filter]");
        if (!chip) return;

        const input = [...form.querySelectorAll("input")].find(
            (el) => el.name === chip.dataset.filter && (el.type !== "checkbox" || el.value === chip.dataset.value),
        );
        if (input.type === "checkbox") input.checked = false;
        else input.value = "";
        applyFilters();
    });

    more?.addEventListener("click", () => load(page + 1, true));

    // Dropdowns
    const dropdowns = root.querySelectorAll("[data-dropdown]");
    const setOpen = (dropdown, open) => {
        dropdown.querySelector(".filter-toggle").setAttribute("aria-expanded", String(open));
        dropdown.querySelector(".filter-options").hidden = !open;
    };

    dropdowns.forEach((dropdown) => {
        dropdown.querySelector(".filter-toggle").addEventListener("click", () => {
            const open = dropdown.querySelector(".filter-toggle").getAttribute("aria-expanded") !== "true";
            dropdowns.forEach((other) => setOpen(other, false));
            setOpen(dropdown, open);
        });
    });

    document.addEventListener("click", (event) => {
        dropdowns.forEach((dropdown) => {
            if (!dropdown.contains(event.target)) setOpen(dropdown, false);
        });
    });

    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") dropdowns.forEach((dropdown) => setOpen(dropdown, false));
    });

    window.addEventListener("popstate", () => {
        writeState(new URLSearchParams(location.search));
        renderChips();
        load(1, false);
    });

    // A shared link carries the filters in its query string
    const initial = new URLSearchParams(location.search);
    if ([...form.querySelectorAll("input")].some((input) => initial.has(input.name))) {
        writeState(initial);
        renderChips();
        load(1, false);
    } else {
        renderChips();
    }
};

const initAll = () => document.querySelectorAll("[data-archive-filter]").forEach(initArchiveFilter);

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
} else {
    initAll();
}
